import {
  UpdateMultipleDirectorInputType,
  updateMultipleDirectors,
  useGetAllDirectorsInCompany,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { PlusIcon, ProceedIcon } from '@commenda/icons';
import {
  Button,
  Loading,
  LoadingIndicator,
  MultiSelect,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import {
  DirectorPersonalDetails,
  TSetNotification,
} from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames } from '@commenda/utils';
import { Country, LegacyDocumentType } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { getDirectorName } from '../Director/common';
import DirectorModal from '../Director/new/DirectorModal';
import { DirectorCardKS, IncorporationFormHeading } from '../common';
import DirectorForm from './DirectorForm';
import { validateDirectorInfo } from './validators';

const DirectorInfo = ({
  next,
  previous,
  data,
  mutate,
  companyId,
  corporationId,
  token,
  setNotification,
  resetFurtherSteps,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  companyId: number;
  corporationId: number;
  token: string;
  setNotification: TSetNotification;
  resetFurtherSteps: () => void;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  // All the directors in the company
  const [directors, setDirectors] = useState<DirectorBase[]>([]);
  const [isSubmitting, setSubmitting] = useState(false);

  const {
    data: companyData,
    error,
    mutate: companyMutate,
  } = useGetAllDirectorsInCompany(data?.companyId);

  const [isDirectorModalShown, setDirectorModalShown] = useState(false);
  const [selectedDirectorIds, setSelectedDirectorIds] = useState<number[]>([]);
  const selectedDirectorIdsRef = useRef<number[]>([]);
  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    const people = companyData?.directors?.map((eachDirector) => {
      const passportFiles = eachDirector?.directorFiles?.filter(
        (file) => file.documentType === LegacyDocumentType.Passport
      );
      const passportFile =
        passportFiles.length > 0
          ? passportFiles[passportFiles.length - 1]
          : null;
      return {
        id: eachDirector?.id,
        directorFirstName: eachDirector.directorFirstName,
        directorMiddleName: eachDirector.directorMiddleName,
        directorLastName: eachDirector.directorLastName,
        email: eachDirector.email,
        operatingAddress: eachDirector.operatingAddress || defaultAddressSchema,
        personalDetails: {
          nationality: (eachDirector.personalDetails as DirectorPersonalDetails)
            ?.nationality as Country,
        },
        number: eachDirector.number,
        corporationHasDirector: eachDirector.corporationHasDirector,
        passportProof: {
          id: passportFile?.id || -1,
          documentType: LegacyDocumentType.Passport,
          fileName: passportFile?.fileName,
          fileUrl: passportFile?.fileUrl,
        },
        directorFiles: eachDirector.directorFiles,
      };
    });

    setDirectors(people || []);
  }, [companyData]);

  useEffect(() => {
    const alreadySelectedDirectors = [...selectedDirectorIdsRef.current];

    companyData?.directors
      ?.filter((dir) =>
        dir.corporationHasDirector?.find(
          (chd) =>
            chd.corporationId === corporationId &&
            chd.directorId === dir.id &&
            chd.additionalDirectorInformation?.isDirector &&
            !chd.additionalDirectorInformation.memberControlType
        )
      )
      .forEach((director) => {
        if (director?.WorkRequests?.find((wrkReq) => wrkReq.id === id)) {
          if (!alreadySelectedDirectors.includes(director.id))
            alreadySelectedDirectors.push(director.id);
        }
      });

    setSelectedDirectorIds(alreadySelectedDirectors);
  }, [companyData]);

  if (!companyData) {
    if (error) {
      console.error(error);
    }
    return <Loading />;
  }

  const onSubmit = async () => {
    setSubmitting(true);

    if (!data?.id) {
      setNotification({
        show: true,
        valid: false,
        message: 'Work request ID is missing. Please refresh the page.',
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    if (selectedDirectorIds.length < 2) {
      setNotification({
        show: true,
        valid: false,
        message: `Singapore requires you to have at least two directors.`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const formError = validateDirectorInfo(directors, selectedDirectorIds);

    if (formError.length) {
      setDirectorIdsWithError(new Set(formError.map((fe) => fe.id)));

      setNotification({
        show: true,
        valid: false,
        message: `Please fill in the missing information for directors by clicking on "Edit".`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const directorsToDisconnect = companyData?.directors
      ?.filter((eachDirector) =>
        eachDirector?.corporationHasDirector?.find(
          (dirWithCorp) =>
            dirWithCorp.corporationId === data.corporationId &&
            dirWithCorp.directorId === eachDirector.id &&
            dirWithCorp.additionalDirectorInformation?.isDirector &&
            !selectedDirectorIds.includes(dirWithCorp.directorId)
        )
      )
      ?.map((each) => each.id);

    const directorUpdates: UpdateMultipleDirectorInputType[] = [
      ...selectedDirectorIds.map((eachDirId) => ({
        id: eachDirId,
        wrkReqId: data.id,
        corporationHasDirector: [
          {
            corporationId: data.corporationId,
            directorId: eachDirId,
            additionalDirectorInformation: {
              isDirector: true,
            },
          },
        ],
      })),
      ...directorsToDisconnect.map((eachDirId) => ({
        id: eachDirId,
        workRequestIdToDisconnect: data.id,
        corporationIdsToDisconnect: [data.corporationId],
      })),
    ];

    const response = await updateMultipleDirectors(directorUpdates, token);

    if (!response) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      setSubmitting(false);
      return;
    }

    await mutate();

    next();

    setSubmitting(false);
  };

  return (
    <div className="flex flex-col gap-6 max-w-[700px]">
      <IncorporationFormHeading
        text="Company Members"
        additionalText="Please provide information about your company&#39;s directors. At least two directors are required."
      />

      {companyData?.directors?.length ? (
        <>
          <span className="mb-[-20px] text-base font-500 text-neutral-1">
            Directors<span className="ml-0.5 text-utility-violet-600">*</span>
          </span>
          <MultiSelect
            id="add-director-multiselect"
            options={(companyData?.directors || []).map((dir) => ({
              label: getDirectorName(dir),
              value: dir.id.toString(),
            }))}
            onValueChange={(val: string[]) => {
              setSelectedDirectorIds(val.map(Number));
              selectedDirectorIdsRef.current = val.map(Number);
              resetFurtherSteps();
            }}
            defaultValue={selectedDirectorIds.map(String)}
            addItemConfig={{
              shouldAllowAdd: true,
              onAddItem: () => setDirectorModalShown(true),
              placeholder: 'Add new director',
            }}
            placeholder="Add or select directors"
            hideTags
            hideSelectAll
          />
        </>
      ) : (
        <>
          <span className="mb-[-20px] text-base font-500 text-neutral-1">
            Directors<span className="ml-0.5 text-utility-violet-600">*</span>
          </span>
          <Button
            variant="secondary"
            className="w-full flex gap-2 items-center justify-center"
            onClick={() => setDirectorModalShown(true)}
          >
            <PlusIcon className="text-neutral-1" />
            <span>Add Director</span>
          </Button>
        </>
      )}

      <div>
        {directors
          ?.filter((dir) => selectedDirectorIds.includes(dir.id))
          .map((director) => (
            <DirectorCardKS
              directorForm={DirectorForm}
              key={director.id}
              director={director}
              companyDataMutate={companyMutate}
              setNotification={setNotification}
              corporationId={corporationId}
              companyId={companyId}
              token={token}
              hasError={directorIdsWithError.has(director.id)}
              onErrorResolve={() =>
                setDirectorIdsWithError((prevIds) => {
                  const newIds = new Set(prevIds);
                  newIds.delete(director.id);
                  return newIds;
                })
              }
              onRemoveDirector={async (directorId: number) => {
                setSelectedDirectorIds((prev) =>
                  prev.filter((dirId) => dirId !== director.id)
                );
                selectedDirectorIdsRef.current = selectedDirectorIds;
                resetFurtherSteps();
              }}
              workRequestData={data}
            />
          ))}
      </div>

      <div className="max-w-[700px]">
        <DirectorModal
          isOpen={isDirectorModalShown}
          setOpen={setDirectorModalShown}
          companyDataMutate={companyMutate}
          DirectorForm={DirectorForm}
          setNotification={setNotification}
          companyId={companyId}
          corporationId={corporationId}
          token={token}
          selectedDirectorIds={selectedDirectorIds}
          setSelectedDirectorIds={setSelectedDirectorIds}
          modalConfig={{
            isDirector: true,
            subheading: 'Fill out the details below to add a new director',
            postSave: (dir) => {
              if (!selectedDirectorIdsRef.current.includes(dir.id)) {
                selectedDirectorIdsRef.current.push(dir.id);
              }
            },
          }}
          workRequestData={data}
        />
      </div>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || directors?.length === 0}
        >
          {isSubmitting ? <LoadingIndicator size={32} /> : null}
          <span className={classNames(isSubmitting ? 'ml-2' : '')}>
            Continue
          </span>
          {!isSubmitting && <ProceedIcon className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default DirectorInfo;
